import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import format from "date-fns/format";
import getMonth from "date-fns/getMonth";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import startOfToday from "date-fns/startOfToday";
import first from "lodash/first";
import groupBy from "lodash/groupBy";
import last from "lodash/last";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import Typography from "@material-ui/core/Typography";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Loader from "components/Loader";
import ScrollSyncPanel from "components/ScrollSync/ScrollSyncPanel";
import { useRacehorse360Api } from "hooks/api";
import IFacilityBlackoutDay from "interfaces/ICalendarDay";
import { SortOrder } from "interfaces/SortOrder";
import WorkoutsPageState from "interfaces/WorkoutsPageState";
import {
  currentTimeZoneString,
  getNextDates,
  mergeDates
} from "utils/date-utils";
import {
  WorkoutRequestOrPlaceHolder,
  isWorkoutRequest
} from "utils/workoutRequest";
import { WORKOUT_REQUEST_GET_OPTIONS } from "../options";
import ScheduleDay from "./ScheduleDay";
import ScheduleHeader from "./ScheduleHeader";

import useStyles from "./styles";

interface ScheduleMonth {
  days: WorkoutRequestOrPlaceHolder[];
  trackedDays: racehorse360.IWorkoutRequest[];
  firstTrackedDay: racehorse360.IWorkoutRequest;
  lastTrackedDay: racehorse360.IWorkoutRequest;
}

export interface Props {
  horse: racehorse360.IHorse;
  facilityBlackoutsCalendar: IFacilityBlackoutDay[];
}

const PAGE_SIZE = 37;
const DAYS_PER_SCREEN = 35;

const HorseWorkoutSchedule = (props: Props) => {
  const { horse, facilityBlackoutsCalendar } = props;

  const classes = useStyles();
  const queryClient = useQueryClient();

  const { scheduledDatesOnly: scheduledOnly } = useSelector(
    (state: { workoutsPage: WorkoutsPageState }) => state?.workoutsPage
  );

  const [skipAnimation, setSkipAnimation] = useState(scheduledOnly);
  const [successFlashCount, setSuccessFlashCount] = useState(0);

  const listWorkoutRequestsQueryKey = `${horse.id}-list-workout-requests`;

  const today = startOfToday();
  const nextDates = getNextDates(today, DAYS_PER_SCREEN).map(date => ({
    date
  }));

  const { useListWorkoutRequests } = useRacehorse360Api();

  const { isLoading, data } = useListWorkoutRequests(
    listWorkoutRequestsQueryKey,
    {
      query: {
        horseIds: [horse.id],
        date: {
          range: {
            relative: {
              // starting two days ago
              startDate: { value: -2 },
              timezone: currentTimeZoneString()
            }
          }
        },
        statuses: [
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_REQUESTED,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_EXAM_PENDING,
          racehorse360.WorkoutRequestStatus.WORKOUT_REQUEST_STATUS_APPROVED
        ]
      },
      pagingOptions: {
        maxResults: PAGE_SIZE
      },
      getOptions: {
        select: WORKOUT_REQUEST_GET_OPTIONS,
        orderBy: [`date ${SortOrder.ASC}`]
      }
    },
    {
      enabled: Boolean(horse?.id),
      initialData: { workoutRequests: [] },
      onError: error => console.error(error)
    }
  );

  const { workoutRequests } = data;

  const schedule = workoutRequests.length
    ? mergeDates(nextDates, workoutRequests)
    : nextDates;

  const scheduleGroups = groupBy(
    schedule,
    day => getYear(parseISO(day.date)) + "-" + getMonth(parseISO(day.date))
  );
  const scheduleGroupKeys = Object.keys(scheduleGroups);

  const scheduleMonths = scheduleGroupKeys.reduce(
    (result, scheduleGroupKey) => {
      const days = scheduleGroups[scheduleGroupKey];
      const trackedDays = days.filter(isWorkoutRequest);
      const firstTrackedDay = first(trackedDays);
      const lastTrackedDay = last(trackedDays);

      result.push({
        days,
        trackedDays,
        firstTrackedDay,
        lastTrackedDay
      });

      return result;
    },
    [] as ScheduleMonth[]
  );

  const hasTrackedDays = schedule.some(isWorkoutRequest);
  const hasNoWorkoutsTitle = scheduledOnly ? !hasTrackedDays : !schedule.length;

  const handleDataRefetch = useCallback(async () => {
    queryClient
      .refetchQueries({
        queryKey: [listWorkoutRequestsQueryKey]
      })
      .then(() => {
        setSuccessFlashCount(successFlashCount + 1);
      })
      .catch(error => {
        console.error(error);
      });
  }, [queryClient]);

  useEffect(() => {
    let timeoutId;
    if (scheduledOnly) {
      setSkipAnimation(false);
    } else {
      timeoutId = setTimeout(() => {
        setSkipAnimation(true);
      }, 300);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [scheduledOnly]);

  return (
    <div
      className={clsx(classes.root, {
        [classes.scheduledOnly]: scheduledOnly,
        [classes.skipAnimation]: skipAnimation
      })}
      data-test-element="HORSE_ROW"
      data-test-value={horse.id}
    >
      <ScheduleHeader
        horse={horse}
        schedule={workoutRequests}
        successFlashCount={successFlashCount}
      />
      {isLoading ? (
        <Loader className={classes.datesList} />
      ) : (
        <ScrollSyncPanel className={clsx(classes.datesList)}>
          {scheduleMonths?.map((scheduleMonth, scheduleMonthIndex) => {
            let hasMonthDivider;
            if (scheduledOnly) {
              if (scheduleMonth.trackedDays.length) {
                scheduleMonths
                  .slice(0, scheduleMonthIndex)
                  .forEach(prevMonth => {
                    hasMonthDivider =
                      hasMonthDivider || !!prevMonth.firstTrackedDay;
                  });
              }
            } else {
              hasMonthDivider = scheduleMonthIndex > 0;
            }

            const daysToShow = scheduledOnly
              ? scheduleMonth.trackedDays
              : scheduleMonth.days;

            return (
              <React.Fragment key={scheduleMonthIndex}>
                <div className={classes.monthGroup}>
                  {hasMonthDivider && (
                    <div className={classes.monthDivider}>
                      <div className={classes.monthDividerText}>
                        {format(
                          parseISO(scheduleMonth.days[0].date),
                          "MMMM"
                        ).toUpperCase()}
                      </div>
                    </div>
                  )}

                  {daysToShow.map(day => {
                    const dataAttributes = {
                      ["data-test-element"]: "CALENDAR_DAY",
                      ["data-test-value"]: day.date
                    };

                    return (
                      <ScheduleDay
                        key={day.date}
                        dayDate={day.date}
                        currentHorseId={horse.id}
                        workoutRequest={isWorkoutRequest(day) ? day : null}
                        facilityBlackoutsCalendar={facilityBlackoutsCalendar}
                        refetchData={handleDataRefetch}
                        {...dataAttributes}
                      />
                    );
                  })}
                </div>
              </React.Fragment>
            );
          })}
          {hasNoWorkoutsTitle && (
            <Typography variant={"caption"} className={classes.noSchedule}>
              No Workouts Scheduled
            </Typography>
          )}
        </ScrollSyncPanel>
      )}
    </div>
  );
};

export default HorseWorkoutSchedule;
